// Allowed use by IPP team.
// eslint-disable-next-line @retailer-platform/no-restricted-imports
import { MantineProvider, type MantineThemeOverride, type MantineColorsTuple } from '@mantine/core'
import React, { useMemo, type FunctionComponent } from 'react'
import { useHistory } from 'react-router'
import {
  ThemeProvider as IDSThemeProvider,
  INSTACART_SANS_FONT_FAMILY,
  FontLoader,
  InstacartSansFontLoaderVariants,
  type ClientSideRoutingProps,
} from '@instacart/ids-core'
import { TdsProvider, type TDS_LOCALES, TDS_DEFAULT_LOCALE } from '../../tds'
import { GlobalStyles } from '../../utils/styling/GlobalStyles'
import { colors } from '../../foundation'
import { breakpoints } from '../../hooks/useGetCurrentBreakpoint.hooks'
// @ts-expect-error TS2882 — strictNullChecks. TODO: fix strict null check
import '@mantine/core/styles.css'
// @ts-expect-error TS2882 — strictNullChecks. TODO: fix strict null check
import '@mantine/dates/styles.css'
// @ts-expect-error TS2882 — strictNullChecks. TODO: fix strict null check
import '@mantine/carousel/styles.css'

export type ThemeProviderProps = {
  locale?: TDS_LOCALES
  /**
   * When true, wires React Router's history into IdsProvider's `routing`
   * prop so all IDS link components (LinkCore, NavigationBar.NavItem)
   * perform SPA navigation via react-aria's useLink instead of full page
   * reloads. Defaults to false to preserve historical behavior — opt in
   * from the consumer when ready.
   */
  enableIdsRouting?: boolean
}

const themeOverrides: MantineThemeOverride = {
  fontFamily: `'${INSTACART_SANS_FONT_FAMILY}'`,
  colors: {
    brand: [
      colors.PRIMARY.REGULAR,
      colors.PRIMARY.DARK,
      colors.PRIMARY.EXTRA_DARK,
    ] as unknown as MantineColorsTuple,
  },
  ...breakpoints,
  respectReducedMotion: false,
  primaryColor: 'brand',
  primaryShade: 0,
  components: {
    // See (v7) Mantine docs https://mantine.dev/styles/styles-api/#classnames-in-themecomponents
    // We apply classNames defined in GlobalStyles to components to override Mantine's default styles
    DatePicker: {
      classNames: {
        calendarHeader: 'internal-mantine-Calendar-calendarHeader',
        calendarHeaderLevel: 'internal-mantine-Calendar-calendarHeaderLevel',
        monthCell: 'internal-mantine-Calendar-monthCell',
        month: 'internal-mantine-Calendar-month',
        yearsListControl: 'internal-mantine-Calendar-yearPickerControl',
        monthsListControl: 'internal-mantine-Calendar-monthPickerControl',
        day: 'internal-mantine-Calendar-day',
        weekday: 'internal-mantine-Calendar-weekday',
      },
      defaultProps: {
        weekendDays: [],
      },
    },
    DateInput: {
      classNames: {
        calendarHeader: 'internal-mantine-Calendar-calendarHeader',
        calendarHeaderLevel: 'internal-mantine-Calendar-calendarHeaderLevel',
        monthCell: 'internal-mantine-Calendar-monthCell',
        month: 'internal-mantine-Calendar-month',
        yearsListControl: 'internal-mantine-Calendar-yearPickerControl',
        monthsListControl: 'internal-mantine-Calendar-monthPickerControl',
        day: 'internal-mantine-Calendar-day',
        weekday: 'internal-mantine-Calendar-weekday',
      },
      defaultProps: {
        weekendDays: [],
      },
    },

    Popover: {
      defaultProps: {
        shadow: 'md',
        withinPortal: true,
      },
      classNames: {
        dropdown: 'mantine-Popover-dropdown',
      },
    },
  },
}

// Bridges React Router v5 history into IdsProvider's `routing` prop so that all IDS
// link components (LinkCore, NavigationBar.NavItem, etc.) perform SPA navigation via
// react-aria's useLink instead of triggering full page reloads. react-aria preserves
// browser-default behavior for modifier-clicks, target=_blank, and external origins.
// Returns undefined when rendered outside a <Router> (tests, Storybook) so IdsProvider
// falls back to native browser navigation in those contexts.
function useIdsProviderRouting(): ClientSideRoutingProps | undefined {
  const history = useHistory()
  return useMemo(() => {
    if (!history) return undefined
    return {
      navigate: to => {
        history.push(to)
      },
      useHref: to => history.createHref({ pathname: to }),
    }
  }, [history])
}

export const ThemeProvider: FunctionComponent<React.PropsWithChildren<ThemeProviderProps>> = ({
  children,
  locale,
  enableIdsRouting = false,
}) => {
  const routing = useIdsProviderRouting()
  return (
    <IDSThemeProvider routing={enableIdsRouting ? routing : undefined}>
      <FontLoader
        fontFamily={INSTACART_SANS_FONT_FAMILY}
        variants={InstacartSansFontLoaderVariants}
      />
      <TdsProvider i18nProps={{ locale: locale || TDS_DEFAULT_LOCALE }}>
        <GlobalStyles />
        <MantineProvider theme={themeOverrides}>{children}</MantineProvider>
      </TdsProvider>
    </IDSThemeProvider>
  )
}
